
/**
 * Module dependencies.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Recaptcha from 'react-google-recaptcha';

/**
 * `Options` type.
 */

type Options = {
  onSubmit: (values: any) => void;
  recaptchaSiteKey: string;
};

/**
 * Export `useRecaptcha`.
 */

export function useRecaptcha({ onSubmit, recaptchaSiteKey }: Options) {
  const ref = useRef<any>();
  const submitRef = useRef(onSubmit);
  const [isExecuting, setExecuting] = useState<boolean>(false);

  useEffect(() => {
    submitRef.current = onSubmit;
  }, [onSubmit]);

  const renderRecaptcha = useCallback(() => (
    <Recaptcha
      ref={ref}
      sitekey={recaptchaSiteKey}
      size={'invisible'}
    />
  ), [recaptchaSiteKey]);

  const submitWithRecaptcha = useCallback(async (values: any) => {
    if (!recaptchaSiteKey || !ref.current) {
      return onSubmit(values);
    }

    setExecuting(true);

    const recaptchaToken = await ref.current.executeAsync();

    try {
      return await onSubmit({
        ...values,
        'g-recaptcha-response': recaptchaToken
      });
    } finally {
      setExecuting(false);
      ref.current.reset();
    }
  }, [onSubmit, recaptchaSiteKey]);

  return {
    isRecaptchaExecuting: isExecuting,
    renderRecaptcha,
    submitWithRecaptcha
  };
}
